import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import PostCreateForm from '@/components/social/PostCreateForm'
import type { SocialPost } from '@/hooks/useSocialMedia'
import { cn } from '@/lib/utils'

export default function CreatePost() {
  const navigate = useNavigate()
  const [duplicateData, setDuplicateData] = useState<Partial<SocialPost> | undefined>()
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    // Check for duplicate post data from sessionStorage
    const savedData = sessionStorage.getItem('duplicatePostData')
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData)
        setDuplicateData(parsedData)
        sessionStorage.removeItem('duplicatePostData') // Clean up
      } catch (error) {
        console.error('Failed to parse duplicate post data:', error)
      }
    }
  }, [])

  const handleBack = () => {
    navigate('/marketing/social')
  }

  const handleSuccess = () => {
    // Show success animation
    setShowSuccess(true)
    
    // Navigate after delay
    setTimeout(() => {
      navigate('/marketing/social')
    }, 2000)
  }

  const handleClose = () => {
    navigate('/marketing/social')
  }

  return (
    <div className="space-y-6">
      {/* Success Overlay */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="animate-in zoom-in-95 duration-300">
            <Card className="w-96">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="rounded-full bg-green-100 p-3">
                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Post Created Successfully!</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Redirecting to your posts...
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className={cn("flex items-center gap-4", showSuccess && "opacity-50")}>
        <Button variant="ghost" size="sm" onClick={handleBack} disabled={showSuccess}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Social Media
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Create New Post</h1>
          <p className="text-muted-foreground">
            Create and schedule social media content for your profiles
          </p>
        </div>
      </div>

      {/* Form Container */}
      <Card className={cn("max-w-4xl", showSuccess && "opacity-50")}>
        <CardHeader>
          <CardTitle>
            {duplicateData ? 'Duplicate Post' : 'Create New Post'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PostCreateForm
            initialData={duplicateData}
            onClose={handleClose}
            onSuccess={handleSuccess}
          />
        </CardContent>
      </Card>
    </div>
  )
}